// client/src/contexts/UserPreferencesContext.js
import React, { createContext, useState, useEffect, useContext } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { createTheme, responsiveFontSizes } from '@mui/material/styles';
import { jwtDecode } from 'jwt-decode';
import axiosInstance from '../axiosInstance';
import { THEMES } from '../constants/themes';
import { FONT_FAMILY } from '../constants/fonts';

const DEFAULT_PREFERENCES = {
  theme: 'indigo',
  accentColor: null,
  fontCategory: 'sans-serif',
  font: 'Inter',
  iconStyle: 'filled',
  collectionOrder: ['games', 'consoles', 'lego', 'movies', 'music', 'books', 'board-games', 'trading-cards', 'manga', 'comics'],
  collectionSettings: {},
};

function buildTheme(preferences) {
  const palette = THEMES[preferences.theme] || THEMES.indigo;
  const primary = preferences.accentColor || palette.primary;

  let theme = createTheme({
    palette: {
      mode: 'dark',
      primary: { main: primary, contrastText: '#0E0F1E' },
      secondary: { main: palette.secondary },
      background: { default: '#0E1022', paper: 'rgba(255,255,255,0.06)' },
      text: { primary: '#E6EAF2', secondary: 'rgba(230,234,242,0.75)' },
    },
    shape: { borderRadius: 18 },
    typography: {
      fontFamily: FONT_FAMILY[preferences.font] || FONT_FAMILY['Inter'],
      h5: { fontWeight: 600 },
      button: { fontWeight: 600, textTransform: 'none' },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            background: 'linear-gradient(135deg, #0F1124 0%, #171935 60%, #101226 100%)',
            color: '#E6EAF2',
            scrollbarColor: '#3A3F5A #121325',
            '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
              backgroundColor: '#121325',
              height: '10px',
              width: '10px',
            },
            '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
              borderRadius: 8,
              background: 'linear-gradient(180deg, #3F4C9A, #6E7BFF)',
              border: '2px solid #121325',
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: 'rgba(255,255,255,0.06)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.12)',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            background: 'linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0.03))',
            border: '1px solid rgba(255,255,255,0.12)',
            boxShadow: '0 10px 30px rgba(0,0,0,0.35)',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 14,
            paddingInline: 18,
            paddingBlock: 8,
            boxShadow: '0 6px 16px rgba(92, 107, 255, 0.35)',
          },
          containedPrimary: {
            background: 'linear-gradient(135deg, #6D7CFF, #9AA6FF)',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 14,
            backgroundColor: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.12)',
          },
          colorPrimary: {
            background: 'linear-gradient(135deg, #2B2F5C, #3C3F7A)',
          },
        },
      },
    },
  });

  return responsiveFontSizes(theme);
}

const UserPreferencesContext = createContext();

export function UserPreferencesProvider({ children }) {
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);
  const [muiTheme, setMuiTheme] = useState(() => buildTheme(DEFAULT_PREFERENCES));

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    let userId;
    try { userId = jwtDecode(token).id; } catch { return; }
    if (!userId) return;

    axiosInstance.get(`/users/${userId}`)
      .then(res => {
        const prefs = {
          ...DEFAULT_PREFERENCES,
          ...(res.data.preferences || {}),
          collectionSettings: res.data.preferences?.collectionSettings || {},
        };
        setPreferences(prefs);
        setMuiTheme(buildTheme(prefs));
      })
      .catch(err => console.error('Failed to load user preferences:', err));
  }, []);

  const updatePreferences = async (patch) => {
    let next;
    setPreferences(prev => {
      next = { ...prev, ...patch };
      return next;
    });
    setMuiTheme(buildTheme(next));
    const token = localStorage.getItem('token');
    if (!token) return;
    let userId;
    try { userId = jwtDecode(token).id; } catch { return; }
    try {
      await axiosInstance.put(`/users/${userId}/preferences`, next);
    } catch (err) {
      console.error('Failed to save preferences:', err);
    }
  };

  const updateCollectionSettings = async (collection, patch) => {
    try {
      const res = await axiosInstance.patch('/users/me/collection-settings', { collection, ...patch });
      setPreferences(prev => ({
        ...prev,
        collectionSettings: { ...prev.collectionSettings, ...res.data.collectionSettings },
      }));
    } catch (err) {
      console.error('Failed to save collection settings:', err);
    }
  };

  const updateCollectionOrder = async (order) => {
    setPreferences(prev => ({ ...prev, collectionOrder: order }));
    try {
      await axiosInstance.put('/users/me/collection-order', { order });
    } catch (err) {
      console.error('Failed to save collection order:', err);
    }
  };

  return (
    <UserPreferencesContext.Provider value={{ preferences, updatePreferences, updateCollectionSettings, updateCollectionOrder }}>
      <ThemeProvider theme={muiTheme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </UserPreferencesContext.Provider>
  );
}

export function useUserPreferences() {
  const ctx = useContext(UserPreferencesContext);
  if (!ctx) throw new Error('useUserPreferences must be used within UserPreferencesProvider');
  return ctx;
}

export default UserPreferencesContext;
